import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import Icon from '../Shared/Icons';
import { getProgram, MEMBER_TYPES } from '../../config/programs';
import { fetchMembers, fetchClubs, fetchReports } from '../../utils/programData';
import { ensureMsaadaMembers } from '../../utils/autoSync';
import { isoWeekKey, fmtDate } from '../../utils/weekUtils';
import { ProgramShell, S, T } from './ui';

// ── WANACHAMA — kwa makundi ya club, kwa herufi au muda ─────────────────
// Kila club ni kundi lake; wasio na club wanakaa kwenye kundi la mwisho.
// Ndani ya kundi, mpangilio ni A-Z (herufi) au wapya-kwanza (muda),
// kutegemea kitufe alichochagua mtumiaji.

const NO_CLUB = '__no_club__';

export default function Members() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const program = getProgram(pid);
  const [members, setMembers] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [reported, setReported] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [type, setType] = useState(params.get('type') || 'all');
  const [sortBy, setSortBy] = useState('name'); // 'name' | 'newest'

  useEffect(() => { if (program) load(); /* eslint-disable-next-line */ }, [pid]);

  async function load() {
    setLoading(true);
    try {
      await ensureMsaadaMembers();
      const [ms, cs, rs] = await Promise.all([
        fetchMembers(pid),
        fetchClubs(pid),
        fetchReports({ programId: pid, kind: 'individual', weekKey: isoWeekKey() }),
      ]);
      setMembers(ms);
      setClubs(cs);
      setReported(new Set(rs.map(r => r.memberId)));
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  if (!program) return <Navigate to="/programs" />;

  const filtered = members.filter(m => {
    if (type !== 'all' && m.type !== type) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (m.fullName || '').toLowerCase().includes(q)
      || (m.school || '').toLowerCase().includes(q)
      || (m.area || '').toLowerCase().includes(q)
      || (m.centre || '').toLowerCase().includes(q)
      || (m.clubName || '').toLowerCase().includes(q);
  });

  // ── Panga kwa club ────────────────────────────────────────────────────
  const groups = {};
  filtered.forEach(m => {
    const key = m.clubId || NO_CLUB;
    (groups[key] = groups[key] || []).push(m);
  });

  function sortGroup(list) {
    const sorted = [...list];
    if (sortBy === 'name') {
      sorted.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
    } else {
      sorted.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    }
    return sorted;
  }

  const clubOrder = clubs
    .filter(c => groups[c.id]?.length)
    .map(c => ({ id: c.id, name: c.name, list: sortGroup(groups[c.id]) }));
  if (groups[NO_CLUB]?.length) {
    clubOrder.push({ id: NO_CLUB, name: 'Hawana Club', list: sortGroup(groups[NO_CLUB]) });
  }

  return (
    <ProgramShell program={program} tab="members">

      <div style={{ display: 'flex', gap: '9px', marginBottom: '14px' }}>
        <input
          style={{ ...S.input, flex: 1 }}
          placeholder="Tafuta jina, shule, eneo, club..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button style={{ ...S.btn, background: program.color, padding: '11px 18px' }}
          onClick={() => navigate(`/programs/${pid}/members/new`)}>
          + Sajili
        </button>
      </div>

      {program.memberTypes.length > 1 && (
        <div style={{ display: 'flex', gap: '7px', marginBottom: '10px', flexWrap: 'wrap' }}>
          {['all', ...program.memberTypes].map(t => (
            <button
              key={t}
              onClick={() => setType(t)}
              style={{
                padding: '8px 15px', borderRadius: '99px', cursor: 'pointer',
                border: `1px solid ${type === t ? program.color : T.line}`,
                background: type === t ? program.color : '#fff',
                color: type === t ? '#fff' : T.muted,
                fontSize: '12px', fontWeight: 700, fontFamily: 'inherit',
              }}
            >
              {t === 'all' ? `Wote (${members.length})` : `${MEMBER_TYPES[t]?.plural} (${members.filter(m => m.type === t).length})`}
            </button>
          ))}
        </div>
      )}

      {/* Mpangilio: herufi au muda wa kusajiliwa */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '16px' }}>
        <span style={{ fontSize: '11.5px', color: T.muted, fontWeight: 600 }}>Panga kwa:</span>
        {[
          { k: 'name', l: 'Herufi (A–Z)' },
          { k: 'newest', l: 'Wapya kwanza' },
        ].map(o => (
          <button
            key={o.k}
            onClick={() => setSortBy(o.k)}
            style={{
              padding: '6px 13px', borderRadius: '99px', cursor: 'pointer',
              border: `1px solid ${sortBy === o.k ? program.color : T.line}`,
              background: sortBy === o.k ? program.color : '#fff',
              color: sortBy === o.k ? '#fff' : T.muted,
              fontSize: '11.5px', fontWeight: 700, fontFamily: 'inherit',
            }}
          >
            {o.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ ...S.card, ...S.empty }}>Inapakia...</div>
      ) : filtered.length === 0 ? (
        <div style={{ ...S.card, ...S.empty }}>
          {members.length === 0
            ? 'Bado hakuna mwanachama kwenye programu hii. Bofya "+ Sajili" kuanza.'
            : 'Hakuna anayeendana na utafutaji wako.'}
        </div>
      ) : clubOrder.map(group => (
        <div key={group.id} style={{ marginBottom: '22px' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '8px 2px', marginBottom: '8px',
          }}>
            <Icon name={group.id === NO_CLUB ? 'user' : 'chess'} size={14} color={program.color} />
            <span style={{ fontSize: '13px', fontWeight: 800, color: program.color }}>
              {group.name}
            </span>
            <span style={{
              fontSize: '11px', fontWeight: 700, color: T.muted,
              background: `${program.color}12`, padding: '2px 9px', borderRadius: '99px',
            }}>
              {group.list.length}
            </span>
          </div>

          {group.list.map(m => {
            const info = m.school || m.centre || m.area || '—';
            const has = reported.has(m.id);
            return (
              <div
                key={m.id}
                onClick={() => navigate(`/programs/${pid}/members/${m.id}`)}
                style={{ ...S.card, cursor: 'pointer', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}
              >
                <div style={{
                  width: '40px', height: '40px', borderRadius: '12px',
                  background: `${program.color}15`, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', flexShrink: 0, fontSize: '15px', fontWeight: 800, color: program.color,
                }}>
                  {(m.fullName || '?').charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: T.ink }}>{m.fullName}</div>
                  <div style={{ fontSize: '11.5px', color: T.muted, marginTop: '3px' }}>
                    {MEMBER_TYPES[m.type]?.label} · {info}
                    {sortBy === 'newest' && m.createdAt ? ` · alisajiliwa ${fmtDate(m.createdAt)}` : ''}
                  </div>
                </div>
                <span style={{
                  ...S.pill,
                  color: has ? '#059669' : '#D97706',
                  background: has ? '#ECFDF5' : '#FFFBEB',
                  whiteSpace: 'nowrap',
                }}>
                  {has ? '✓ Wiki hii' : 'Bado'}
                </span>
              </div>
            );
          })}
        </div>
      ))}

      {!loading && members.length > 0 && (
        <div style={{ textAlign: 'center', fontSize: '12px', color: T.muted, marginTop: '10px' }}>
          <Icon name="info" size={12} /> Wanachama {filtered.length} kati ya {members.length}
        </div>
      )}

    </ProgramShell>
  );
}
